import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  Pencil,
  Trash2,
  QrCode,
  Download,
  Handshake,
} from "lucide-react";
import { EmptyState } from "@/components/brand/EmptyState";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { PartnerDto } from "@/types/partner";
import { partnerService } from "@/services/partnerService";
import { toast } from "@/hooks/use-toast";
import { generatePartnerQRCode, downloadQRCode } from "@/utils/qrCodeGenerator";
import PartnerFormDialog from "./PartnerFormDialog";

const Partners: React.FC = () => {
  const [partners, setPartners] = useState<PartnerDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<PartnerDto | null>(null);
  const [deletingPartner, setDeletingPartner] = useState<PartnerDto | null>(
    null,
  );
  const [generatingQR, setGeneratingQR] = useState<string | null>(null);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const data = await partnerService.getAllPartners();
      setPartners(data);
    } catch (error) {
      console.error("Error fetching partners:", error);
      toast({
        title: "Error",
        description: "Failed to fetch partners",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  const handleCreate = () => {
    setEditingPartner(null);
    setIsFormOpen(true);
  };

  const handleEdit = (partner: PartnerDto) => {
    setEditingPartner(partner);
    setIsFormOpen(true);
  };

  const handleDelete = async (partner: PartnerDto) => {
    if (!partner.id) return;

    try {
      await partnerService.deletePartner(partner.id);
      setPartners(partners.filter((p) => p.id !== partner.id));
      toast({
        title: "Success",
        description: "Partner deleted successfully",
      });
    } catch (error) {
      console.error("Error deleting partner:", error);
      toast({
        title: "Error",
        description: "Failed to delete partner",
        variant: "destructive",
      });
    }
    setDeletingPartner(null);
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setEditingPartner(null);
    fetchPartners();
  };

  const handleGenerateQR = async (partner: PartnerDto) => {
    try {
      setGeneratingQR(partner.id || "");
      const qrCodeDataUrl = await generatePartnerQRCode(partner.referralCode);
      downloadQRCode(qrCodeDataUrl, partner.referralCode);
      toast({
        title: "Success",
        description: "QR code downloaded successfully",
      });
    } catch (error) {
      console.error("Error generating QR code:", error);
      toast({
        title: "Error",
        description: "Failed to generate QR code",
        variant: "destructive",
      });
    } finally {
      setGeneratingQR(null);
    }
  };

  const getStatusBadge = (status: "ACTIVE" | "INACTIVE") => {
    return (
      <Badge variant={status === "ACTIVE" ? "default" : "secondary"}>
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Partners</h1>
          <p className="text-muted-foreground mt-1">
            Manage your referral partners
          </p>
        </div>
        <Button
          onClick={handleCreate}
          className="bg-primary hover:bg-primary/90"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Partner
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Partners</CardTitle>
          <CardDescription>
            View and manage your referral partners
          </CardDescription>
        </CardHeader>
        <CardContent>
          {partners.length === 0 ? (
            <EmptyState
              icon={Handshake}
              title="No partners yet"
              description="Referral partners send work your way and are credited automatically when a bid quotes their code."
              action={
                <Button onClick={handleCreate}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Your First Partner
                </Button>
              }
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Referral Code</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Referral %</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {partners.map((partner) => (
                  <TableRow key={partner.id}>
                    <TableCell className="font-medium">
                      {partner.referralCode}
                    </TableCell>
                    <TableCell>{partner.companyName || "-"}</TableCell>
                    <TableCell>{partner.contactName}</TableCell>
                    <TableCell>{partner.email}</TableCell>
                    <TableCell>{partner.phone || "-"}</TableCell>
                    <TableCell>{partner.referralPercentage}%</TableCell>
                    <TableCell>{getStatusBadge(partner.status)}</TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(partner)}
                          title="Edit Partner"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleGenerateQR(partner)}
                          disabled={generatingQR === partner.id}
                          title="Generate QR Code"
                        >
                          {generatingQR === partner.id ? (
                            <Download className="h-4 w-4 animate-spin" />
                          ) : (
                            <QrCode className="h-4 w-4" />
                          )}
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setDeletingPartner(partner)}
                              title="Delete Partner"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Delete Partner
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "
                                {partner.companyName || partner.contactName}"?
                                This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(partner)}
                                className="bg-destructive hover:bg-destructive/90"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {isFormOpen && (
        <PartnerFormDialog
          partner={editingPartner}
          isOpen={isFormOpen}
          onClose={() => {
            setIsFormOpen(false);
            setEditingPartner(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default Partners;
